import React, { useState, useEffect } from "react";
import { Grid, Card, CardContent, Typography, Button, Box } from "@mui/material";
import { MeetingRoom, CalendarToday, ArrowForward, Devices } from "@mui/icons-material";
import Footer from "../Footer/Footer";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import dayjs from "dayjs";
import { format, formatISO } from "date-fns";
import { keyframes } from "@emotion/react";

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60% { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
`;

export const Dashboard = () => {
  const [isHoveredRoom, setIsHoveredRoom] = useState(false);
  const [isHoveredReserve, setIsHoveredReserve] = useState(false);
  const [isHoveredEquipmentReserve, setIsHoveredEquipmentReserve] = useState(false);
  const [totalBoardroom, setTotalBoardroom] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [EmpCode, setEmpCode] = useState(null);
  const [totalReservation, setTotalReservation] = useState([]);
  const [totalEquipmentReservation, setTotalEquipmentReservation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/boardroom/getAvailableBoardrooms`);
        if (Array.isArray(response.data)) {
          console.warn("Response is an array. Accessing first element.");
          response.data = response.data[0];
        }
        const totalRooms = Object.values(response.data || {}).reduce(
          (sum, rooms) => (Array.isArray(rooms) ? sum + rooms.length : sum),
          0
        );

        setTotalBoardroom(totalRooms);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data.");
        setLoading(false);
      }
    };

    const fetchReservations = async () => {
      try {
        const startDate = new Date();
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date();
        endDate.setMonth(startDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);

        const response = await axios.get(`${config.API_BASE_URL}/boardroomReservation/getReservationsByDate`, {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          },
        });

        const fetchedEvents = response.data.map((res) => ({
          id: res.reservationId,
          title: res.roomName,
          selectedLocation: res.selectedLocation,
          reservedBy: res.reserveBy,
          start: new Date(res.selectedStartDate),
          end: new Date(res.selectedEndDate),
          allDay: false,
          EmpCode: res.EmpCode,
          remarks: res.remarks,
          status: res.status,
        }));

        console.log("All Fetched Reservations:", fetchedEvents);
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const filteredReservations = fetchedEvents.filter((event) => {
          const eventMonth = event.start.getMonth();
          const eventYear = event.start.getFullYear();
          return eventMonth === currentMonth && eventYear === currentYear;
        });

        console.log("Reservations in Current Month:", filteredReservations.length);
        setTotalReservation(filteredReservations.length);
      } catch (error) {
        console.error("Error fetching reservations:", error);
      }
    };

    const fetchEquipmentReservations = async () => {
      try {
        const startDate = new Date();
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date();
        endDate.setMonth(startDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);

        const response = await axios.get(`${config.API_BASE_URL}/equipmentReservation/getEquipmentReservations`, {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          },
        });

        const fetchedEvents = response.data.map((res) => ({
          id: res.equipmentReservationId,
          title: res.equipmentName,
          selectedLocation: res.selectedLocation,
          reservedBy: res.reserveBy,
          start: new Date(res.selectedStartDate),
          end: new Date(res.selectedEndDate),
          allDay: false,
          EmpCode: res.EmpCode,
          status: res.status,
        }));

        // console.log("All Fetched Equipment Reservations:", fetchedEvents);
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const filteredReservations = fetchedEvents.filter((event) => {
          const eventMonth = event.start.getMonth();
          const eventYear = event.start.getFullYear();
          return eventMonth === currentMonth && eventYear === currentYear;
        });

        // console.log("Equipment Reservations in Current Month:", filteredReservations.length);
        setTotalEquipmentReservation(filteredReservations.length);
      } catch (error) {
        console.error("Error fetching reservations:", error);
      }
     };

    fetchData();
    fetchReservations();
    fetchEquipmentReservations();
  }, []);


  useEffect(() => {
    try {
      const rawAccessLevel = localStorage.getItem("Boardroom_AccessLevel");
      const accessLevel = rawAccessLevel
        ? rawAccessLevel.replace(/"/g, "")
        : null;

      const storedEmpCode = localStorage.getItem("EmpCode");

      if (accessLevel) {
        // console.log("Access Level:", rawAccessLevel);
        setLoggedInUser(accessLevel);
      } else {
        console.warn("Invalid AccessLevel format in localStorage.");
      }

      if (storedEmpCode) {
        setEmpCode(storedEmpCode);
      }
    } catch (error) {
      console.error("Error parsing localStorage data:", error);
    }
  }, []);

  const handleMoreInfoRoom = () => {
    navigate("/assembly-hall");
  };

  const handleMoreInfoReserve = () => {
    navigate("/schedules");
  };

  const handleMoreInfoEquipmentReserve = () => {
    navigate("/equipments");
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, padding: "20px", paddingLeft: "250px", paddingTop: "55px" }}>

      <Box sx={{ flexGrow: 1, padding: "20px" }}>
        <Typography variant="h4"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          gutterBottom>
          Welcome to Online Reservation System
          <Box
            component="span"
            sx={{
              display: "inline-block",
              animation: `${waveAnimation} 4s infinite`,
              transformOrigin: "70% 70%",
            }}
            role="img"
            aria-label="wave"
          >
            👋
          </Box>
        </Typography>
        <hr />

        <Grid container spacing={3}>
          {loggedInUser === "ADMIN" ? (
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  backgroundColor: "#17a2b8",
                  color: "white",
                  borderRadius: 1,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <CardContent
                  sx={{
                    padding: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {totalBoardroom}
                    </Typography>
                    <Typography variant="subtitle1">Total Board-Rooms</Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      right: 10,
                      transition: "transform 0.3s ease",
                      transform: isHoveredRoom ? "scale(1.2)" : "scale(1)",
                    }}
                  >
                    <MeetingRoom sx={{ fontSize: 70, color: "rgba(20, 138, 157, 1)" }} />
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    backgroundColor: "#1591a5",
                    textAlign: "center",
                    padding: "2px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                    "&:hover": { backgroundColor: "#107784" },
                  }}
                  onMouseEnter={() => setIsHoveredRoom(true)}
                  onMouseLeave={() => setIsHoveredRoom(false)}
                >
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleMoreInfoRoom}
                    sx={{
                      textTransform: "none",
                      display: "flex",
                      alignItems: "center",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    More info
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: 1,
                      }}
                    >
                      <ArrowForward sx={{ fontSize: 16, fontWeight: "bold", color: "#17a2b8" }} />
                    </Box>
                  </Button>
                </Box>
              </Card>
            </Grid>
          ) : (
            <></>
          )
          }

          <Grid item xs={12} sm={6} lg={3.5}>
            <Card
              sx={{
                backgroundColor: "#28a745",
                color: "white",
                borderRadius: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardContent
                sx={{
                  padding: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {totalReservation}
                  </Typography>
                  <Typography variant="subtitle1">Boardroom bookings this month</Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    right: 10,
                    transition: "transform 0.3s ease",
                    transform: isHoveredReserve ? "scale(1.2)" : "scale(1)",
                  }}
                >
                  <CalendarToday sx={{ fontSize: 70, color: "rgba(34, 142, 59, 1)" }} />
                </Box>
              </CardContent>

              <Box
                sx={{
                  backgroundColor: "#1e7e34",
                  textAlign: "center",
                  padding: "2px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  "&:hover": { backgroundColor: "#155d27" },
                }}
                onMouseEnter={() => setIsHoveredReserve(true)}
                onMouseLeave={() => setIsHoveredReserve(false)}
              >
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleMoreInfoReserve}
                  sx={{
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  More info
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: 1,
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 16, fontWeight: "bold", color: "#28a745" }} />
                  </Box>
                </Button>
              </Box>
            </Card>
          </Grid>

   <Grid item xs={12} sm={6} lg={3.5}>
            <Card
              sx={{
                backgroundColor: "#4daefe",
                color: "white",
                borderRadius: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardContent
                sx={{
                  padding: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {totalEquipmentReservation}
                  </Typography>
                  <Typography variant="subtitle1">Equipment bookings this month</Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    right: 10,
                    transition: "transform 0.3s ease",
                    transform: isHoveredEquipmentReserve ? "scale(1.2)" : "scale(1)",
                  }}
                >
                  <Devices sx={{ fontSize: 70, color: "#0056b3" }} />
                </Box>
              </CardContent>

              <Box
                sx={{
                  backgroundColor: "#0056b3",
                  textAlign: "center",
                  padding: "2px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  "&:hover": { backgroundColor: "#003d80" },
                }}
                onMouseEnter={() => setIsHoveredEquipmentReserve(true)}
                onMouseLeave={() => setIsHoveredEquipmentReserve(false)}
              >
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleMoreInfoEquipmentReserve}
                  sx={{
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  More info
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: 1,
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 16, fontWeight: "bold", color: "#0080d4" }} />
                  </Box>
                </Button>
              </Box>
            </Card>
          </Grid>  
        </Grid>
      </Box>
      <Footer />
    </Box>
  );
};

export default Dashboard;
